// Global Instructions Rule Applied!
//
// Shared editor for the two agent jsonb "bag" columns:
//   * cfg.agent_tools.value_caps                (Agent Gateway → Tools)
//   * cfg.internal_agent_task_models.params     (Internal Agents → Task routing)
//
// Both are the same problem — edit a whitelisted, context-dependent set of
// scalar keys on a free-form jsonb blob — so this is one parameterised
// component rather than two near-identical modals (CLAUDE.md: generic
// functions, appropriately parameterised; a customised component becomes a
// local standard and gets reused).
//
// The field spec comes from cfg.agent_config_value_keys, already filtered by
// context (tool kind / model provider) by the read RPC, so the editor never
// offers a setting the runtime would ignore.
//
// TWO BEHAVIOURS WORTH STATING:
//   * MERGE, never blind replace. The draft starts as a copy of the stored
//     object and only the spec'd keys are written, so a key the whitelist does
//     not yet know about survives an edit instead of being silently dropped.
//     Those keys are listed under the fields so the change is visible.
//   * Blank = remove. Clearing an input deletes the key from the object rather
//     than storing null, which is what makes "unset this cap" reachable at all
//     — the setter RPC stores the validated subset it is handed.
//
// Chrome is the in-module modal grammar from PipelinesPanel: label/control rows
// in a flex column, secondary hint text beneath, AntD's default [Cancel][Save]
// footer.
import React, { useEffect, useMemo, useState } from 'react'
import { InputNumber, Modal, Select, Typography } from 'antd'

const { Text } = Typography

export interface JsonFieldSpec {
  /** The jsonb key, e.g. 'max_rows' or 'effort'. */
  key: string
  label: string
  type: 'number' | 'select'
  options?: { value: string; label: string }[]
  min?: number
  max?: number
  hint?: string
}

interface JsonFieldsModalProps {
  open: boolean
  title: string
  description?: React.ReactNode
  fields: JsonFieldSpec[]
  /** The stored object. Keys outside `fields` are preserved. */
  value: Record<string, unknown>
  saving: boolean
  onCancel: () => void
  onSave: (next: Record<string, unknown>) => void
}

export default function JsonFieldsModal({
  open,
  title,
  description,
  fields,
  value,
  saving,
  onCancel,
  onSave
}: JsonFieldsModalProps): React.ReactElement {
  const [draft, setDraft] = useState<Record<string, unknown>>({})

  // Re-seed whenever the modal opens on a different row. destroyOnClose alone
  // would not do this — the component stays mounted while `open` flips.
  useEffect(() => {
    if (open) setDraft({ ...(value || {}) })
  }, [open, value])

  // Keys present in the stored object that this editor does not manage. Shown
  // rather than hidden, so "I saved and something I never touched changed" can
  // never be a surprise.
  const unmanagedKeys = useMemo(() => {
    const managed = new Set(fields.map((f) => f.key))
    return Object.keys(draft).filter((k) => !managed.has(k))
  }, [draft, fields])

  const setKey = (key: string, next: unknown): void => {
    setDraft((prev) => {
      const copy = { ...prev }
      if (next === null || next === undefined || next === '') {
        delete copy[key]
      } else {
        copy[key] = next
      }
      return copy
    })
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={() => onSave(draft)}
      okText='Save'
      confirmLoading={saving}
      width={520}
      destroyOnClose
    >
      {description ? (
        <div className='mb-3'>
          <Text type='secondary' style={{ fontSize: 12 }}>
            {description}
          </Text>
        </div>
      ) : null}

      <div className='flex flex-col gap-3'>
        {fields.length === 0 ? (
          <Text type='secondary'>No editable settings for this row.</Text>
        ) : null}

        {fields.map((f) => (
          <div key={f.key}>
            <div className='flex items-center justify-between gap-3'>
              <Text>{f.label}</Text>
              {f.type === 'select' ? (
                <Select
                  allowClear
                  size='small'
                  placeholder='Not set'
                  className='min-w-[160px]'
                  value={(draft[f.key] as string) ?? undefined}
                  options={f.options || []}
                  onChange={(next) => setKey(f.key, next ?? null)}
                />
              ) : (
                <InputNumber
                  size='small'
                  placeholder='Not set'
                  min={f.min}
                  max={f.max}
                  style={{ width: 160 }}
                  controls={false}
                  value={(draft[f.key] as number) ?? undefined}
                  onChange={(next) => setKey(f.key, typeof next === 'number' ? next : null)}
                />
              )}
            </div>
            {f.hint ? (
              <Text type='secondary' style={{ fontSize: 12 }}>
                {f.hint}
              </Text>
            ) : null}
          </div>
        ))}

        <Text type='secondary' style={{ fontSize: 12 }}>
          Leave a field blank to remove that setting.
        </Text>

        {unmanagedKeys.length ? (
          <Text type='secondary' style={{ fontSize: 12 }}>
            Also set, kept as-is: {unmanagedKeys.join(', ')}
          </Text>
        ) : null}
      </div>
    </Modal>
  )
}
